import { CellClassParams, ColDef, ValueSetterParams } from '@ag-grid-community/core';
import { ICellRendererParams, IGridColumn, IGridProps } from 'es/app/components/data-grid/Grid.types';
import ActionCell from 'es/app/components/data-grid/ActionCell';

export function useGridColumnDefs<TItem extends Record<string, any>>(props: IGridProps<TItem>) {
    const { columns } = props;

    const columnDefs = columns.flatMap(column => {
        const col = buildColumnDef(column);
        if (!col) return [];
        return col;
    });

    const showModifiedCell = !!props.rowModified;

    if (showModifiedCell) {
        const modifiedCellClassRules = {
            modified: ({ data }: CellClassParams<TItem>) => {
                if (!data) return false;
                return props.rowModified?.(data) ?? false;
            },
        };

        columnDefs.unshift({
            colId: '__modified',
            minWidth: 5,
            maxWidth: 5,
            headerName: '',
            lockPosition: true,
            suppressMovable: true,
            filter: false,
            sortable: false,
            resizable: false,
            editable: false,
            headerClass: 'modified-cell',
            cellClass: 'modified-cell',
            cellClassRules: modifiedCellClassRules,
        });
    }

    const showActionCell = !!(props.onEdit || props.onDelete || props.onRefresh || props.CustomAction);

    if (showActionCell) {
        columnDefs.push({
            colId: '__grid-actions',
            minWidth: 95,
            maxWidth: 95,
            resizable: false,
            suppressMovable: true,
            pinned: 'right',
            cellRenderer: (params: ICellRendererParams<TItem>) => {
                const { onEdit, canEdit, onDelete, canDelete, showRowActions, CustomAction } = props;
                if (showRowActions && !showRowActions(params.data)) return '';
                return (
                    <ActionCell<TItem>
                        {...params}
                        onEdit={onEdit}
                        canEdit={canEdit}
                        onDelete={onDelete}
                        canDelete={canDelete}
                        CustomAction={CustomAction}
                    />
                );
            },
            sortable: false,
        });
    }

    return columnDefs;
}

function buildColumnDef<TItem extends unknown>(column: IGridColumn<TItem>) {
    const { id, field, cell, cellContentRenderer, onChanging, fixedWidth, hidden, collapse, ...rest } = column;
    if (hidden) return null;

    const colDef: ColDef<TItem> = {
        ...rest,
        colId: id ?? field,
        field,
    };

    if (fixedWidth !== null && fixedWidth !== undefined) {
        colDef.minWidth = colDef.maxWidth = fixedWidth;
        colDef.suppressAutoSize = true;
        colDef.resizable = false;
    }

    if (cellContentRenderer && !cell) {
        colDef.cellRenderer = (cellProps: ICellRendererParams<TItem>) =>
            cellContentRenderer(cellProps.value, cellProps.data);
    }

    if (cell) {
        colDef.cellRenderer = cell;
        colDef.cellClassRules = {
            ...(colDef.cellClassRules || {}),
            'custom-cell': () => true,
        };
    }

    if (onChanging) {
        colDef.valueSetter = (params: ValueSetterParams) => {
            onChanging(params.data, params.newValue, params.oldValue);
            return true;
        };
    }

    return colDef;
}
